import React, { useState, useEffect, useCallback } from "react";
import { MdChevronLeft, MdChevronRight, MdToday } from "react-icons/md";
import Card from "components/card";
import CalendarGrid from "./components/CalendarGrid";
import EventModal from "./components/EventModal";
import axios from "utils/axiosConfig";
import { buildApiUrl } from "utils/api";
import { getToken } from "utils/auth";

const Calendar = () => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [events, setEvents] = useState({});
    const [loading, setLoading] = useState(false);

    const getDateKey = (date) => {
        const localDate = new Date(date);
        if (Number.isNaN(localDate.getTime())) return null;
        const year = localDate.getFullYear();
        const month = String(localDate.getMonth() + 1).padStart(2, "0");
        const day = String(localDate.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const parseScheduleDate = (value) => {
        if (!value) return null;
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }

        let parsedDate = new Date(value);
        if (!Number.isNaN(parsedDate.getTime())) {
            return parsedDate;
        }

        if (typeof value === "string") {
            const normalized = value.trim().replace(" ", "T");
            parsedDate = new Date(normalized);
            if (!Number.isNaN(parsedDate.getTime())) {
                return parsedDate;
            }
        }

        return null;
    };

    const fetchSchedules = useCallback(async (date) => {
        try {
            setLoading(true);
            const year = date.getFullYear();
            const month = date.getMonth() + 1; // API expects 1-based month
            const token = getToken();

            const headers = {};
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
            
            const response = await axios.get(buildApiUrl(`/clients/schedules?year=${year}&month=${month}`), { headers });
            
            if (response?.data?.success && response?.data?.data) {
                const { clientSchedules = [], leadSchedules = [] } = response.data.data;
                const formattedEvents = {};

                // Process client schedules
                clientSchedules.forEach((schedule) => {
                    const scheduleDate = parseScheduleDate(schedule.scheduledTime);
                    const dateKey = getDateKey(scheduleDate);
                    if (!dateKey) return;

                    if (!formattedEvents[dateKey]) {
                        formattedEvents[dateKey] = [];
                    }

                    formattedEvents[dateKey].push({
                        id: schedule.scheduleId,
                        type: "client",
                        name: schedule.name,
                        phone: schedule.phone,
                        email: schedule.email,
                        date: scheduleDate,
                        time: scheduleDate,
                        note: schedule.scheduleNote,
                        status: schedule.scheduleStatus,
                        careOf: schedule.careOfName,
                    });
                });

                // Process lead schedules
                leadSchedules.forEach((schedule) => {
                    const scheduleDate = parseScheduleDate(schedule.scheduledTime);
                    const dateKey = getDateKey(scheduleDate);
                    if (!dateKey) return;

                    if (!formattedEvents[dateKey]) {
                        formattedEvents[dateKey] = [];
                    }

                    formattedEvents[dateKey].push({
                        id: schedule.scheduleId,
                        type: "lead",
                        name: schedule.name,
                        phone: schedule.phone,
                        email: schedule.email,
                        date: scheduleDate,
                        time: scheduleDate,
                        note: schedule.scheduleNote,
                        status: schedule.scheduleStatus,
                        careOf: schedule.careOfName,
                    });
                });

                setEvents(formattedEvents);
            }
        } catch (error) {
            console.error("Error fetching schedules:", error);
            setEvents({});
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch schedules when component mounts or date changes
    useEffect(() => {
        fetchSchedules(currentDate);
    }, [currentDate, fetchSchedules]);

    // Navigate to previous month
    const handlePrevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    // Navigate to next month
    const handleNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    // Jump to today
    const handleToday = () => {
        setCurrentDate(new Date());
    };

    // Handle month change
    const handleMonthChange = (e) => {
        const newMonth = parseInt(e.target.value, 10);
        setCurrentDate(new Date(currentDate.getFullYear(), newMonth, 1));
    };

    // Handle year change
    const handleYearChange = (e) => {
        const newYear = parseInt(e.target.value, 10);
        setCurrentDate(new Date(newYear, currentDate.getMonth(), 1));
    };

    // Generate year range (current year ±10)
    const currentYear = new Date().getFullYear();
    const yearRange = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);

    // Month names
    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    // Format month/year display
    const monthName = currentDate.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
    });

    return (
        <div className="flex flex-col gap-5 w-full">
            {/* Header with navigation */}
            <Card extra="rounded-[20px] p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    {/* Left: Month/Year Selectors */}
                    <div className="flex items-center gap-3 flex-wrap">
                        <h2 className="text-2xl font-bold text-navy-700 dark:text-white hidden sm:block">
                            {monthName}
                        </h2>

                        {/* Month Dropdown */}
                        <select
                            value={currentDate.getMonth()}
                            onChange={handleMonthChange}
                            className="px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-navy-700 dark:text-white font-medium transition-all duration-200 hover:border-brand-500 focus:border-brand-500 focus:outline-none cursor-pointer text-sm"
                        >
                            {months.map((month, idx) => (
                                <option key={idx} value={idx}>
                                    {month}
                                </option>
                            ))}
                        </select>

                        {/* Year Dropdown */}
                        <select
                            value={currentDate.getFullYear()}
                            onChange={handleYearChange}
                            className="px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-navy-700 dark:text-white font-medium transition-all duration-200 hover:border-brand-500 focus:border-brand-500 focus:outline-none cursor-pointer text-sm"
                        >
                            {yearRange.map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Right: Navigation Buttons */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleToday}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-white hover:bg-brand-600 transition-all duration-200 font-medium text-sm whitespace-nowrap"
                        >
                            <MdToday className="h-5 w-5" />
                            <span className="hidden sm:inline">Today</span>
                        </button>
                        <button
                            onClick={handlePrevMonth}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
                            title="Previous month"
                        >
                            <MdChevronLeft className="h-6 w-6 text-navy-700 dark:text-white" />
                        </button>
                        <button
                            onClick={handleNextMonth}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
                            title="Next month"
                        >
                            <MdChevronRight className="h-6 w-6 text-navy-700 dark:text-white" />
                        </button>
                    </div>
                </div>
            </Card>

            {/* Calendar Grid - Full Width */}
            <CalendarGrid
                currentDate={currentDate}
                events={events}
                onEventClick={setSelectedEvent}
                loading={loading}
            />

            {/* Event Detail Modal */}
            {selectedEvent && (
                <EventModal
                    event={selectedEvent}
                    onClose={() => setSelectedEvent(null)}
                />
            )}
        </div>
    );
};

export default Calendar;
